import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import PageLayout from "components/layout/page-layout";
import { useSharedState } from "state";
import { AgGridReact } from "ag-grid-react";
import { TreeDataModule } from "ag-grid-enterprise";
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ColDef,
  ColumnAutoSizeModule,
  ModuleRegistry,
  RowAutoHeightModule,
  ValidationModule,
  ValueFormatterParams,
} from "ag-grid-community";
import moment from "moment";
import compactNumber from "helpers/compact-number";
import { downloadPlanFactToExcel, saveAutocorrection } from "api/budget_request";

ModuleRegistry.registerModules([
  AllCommunityModule,
  TreeDataModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  RowAutoHeightModule,
  ValidationModule,
]);

export default function Summary() {
  const gridRef = useRef<AgGridReact>(null);
  const { isAdmin, summaryTable, getSummaryTableData, notify, filtersList } = useSharedState();

  const navigate = useNavigate();

  const [autocorrectionValues, setAutocorrectionValues] = useState<number[]>([]);
  const [changedRows, setChangedRows] = useState<Map<number, number>>(new Map());
  const [isAutocorrectionChanged, setAutocorrectionChanged] = useState<boolean>(false);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);

  const isFiltersValid = useMemo(() => {
    return !!filtersList.company_id && !!filtersList.department_id && !!filtersList.year;
  }, [filtersList]);

  useEffect(() => {
    getSummaryTableData();
  }, []);

  useEffect(() => {
    if (summaryTable.rows.length) {
      setAutocorrectionValues(summaryTable.rows.map((row) => row.autocorrection_total));
      setChangedRows(new Map());
      setAutocorrectionChanged(false);
    }
  }, [summaryTable]);

  useEffect(() => {
    setAutocorrectionChanged(changedRows.size > 0);
  }, [changedRows]);

  const onSaveAutocorrection = async () => {
    try {
      setFormLoading(true);

      const payload = Array.from(changedRows.entries()).map(([rowIndex, correctionValue]) => {
        const row = summaryTable.rows[rowIndex];
        return {
          budget_level_id_path: row.budget_level_id_path,
          company_id: filtersList.company_id ? parseInt(filtersList.company_id) : undefined,
          department_id: filtersList.department_id ? parseInt(filtersList.department_id) : undefined,
          year: filtersList.year ? parseInt(filtersList.year) : undefined,
          correction_value: correctionValue,
        };
      });

      const params = new URLSearchParams();
      filtersList.company_id && params.append("company_id", filtersList.company_id);
      filtersList.department_id && params.append("department_id", filtersList.department_id);
      filtersList.year && params.append("year", filtersList.year);

      await saveAutocorrection(payload);
      await getSummaryTableData(params);

      notify("Автокорректировка сохранена", "success");
      setChangedRows(new Map());
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при сохранении автокорректировки");
    } finally {
      setFormLoading(false);
    }
  };

  const onExcelDownload = async () => {
    try {
      const payload = {};
      const res: any = await downloadPlanFactToExcel(payload);

      const fileName = `Сводная таблица ${moment().format("DD.MM.YYYY HH:mm")}.xlsx`;
      const blob = new Blob([res], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName.replaceAll(" ", "_");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      notify("Скачивание завершено", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении файла");
    }
  };

  const reportTableRows = useMemo(
    () => [
      ...summaryTable.rows.map((row, rowIndex) => ({
        ...row,
        id: rowIndex,
        path: row.budget_level_name_path ? [...row.budget_level_name_path.split("/")] : [row.display_name],
        remainder: row.budget_amount - (autocorrectionValues[rowIndex] ?? row.autocorrection_total ?? 0),
      })),
    ],
    [summaryTable, autocorrectionValues]
  );

  const reportTableColumns: ColDef[] = useMemo(() => {
    const columns = [...Object.keys(summaryTable.header)]
      .filter(
        (col) =>
          col !== "budget_level_id_path" && col !== "current_budget_level" && col !== "depth" && col !== "display_name"
      )
      .map(
        (column) =>
          summaryTable.rows.length && {
            field: column,
            headerName: column === "autocorrection_total" ? "Автокорректировка" : summaryTable.header[column],
            editable: (params: any) =>
              isAdmin &&
              column === "autocorrection_total" &&
              params.data.is_leaf === true &&
              params.data.budget_level_name_path !== "Unknown",
            cellEditor:
              isAdmin && isFiltersValid && column === "autocorrection_total" ? "agNumberCellEditor" : undefined,
            valueFormatter: (params: ValueFormatterParams) =>
              !column.includes("budget_level_id_path") ||
              !column.includes("current_budget_level") ||
              !column.includes("depth")
                ? compactNumber(params.value || "") || 0
                : params.value,
          }
      )
      .filter(Boolean) as ColDef[];

    // Insert remainder column after autocorrection_total
    const autocorrectionIndex = columns.findIndex((col) => col.field === "autocorrection_total");
    if (autocorrectionIndex !== -1) {
      const remainderColumn: ColDef = {
        field: "remainder",
        headerName: "Остаток",
        editable: false,
        valueFormatter: (params: ValueFormatterParams) => compactNumber(params.value || "") || "0",
      };
      columns.splice(autocorrectionIndex + 1, 0, remainderColumn);
    }

    return columns;
  }, [summaryTable, isAdmin, isFiltersValid]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      minWidth: 150,
      resizable: true,
      autoHeight: true,
      editable: false,
    };
  }, []);

  const getRowStyle = useCallback(
    (params: any) => {
      if (isAdmin && params.data.is_leaf === true && params.data.budget_level_name_path !== "Unknown") {
        return { backgroundColor: "#f0f9ff" };
      }
      return undefined;
    },
    [isAdmin]
  );

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitCellContents" as const,
    };
  }, []);

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      width: 400,
      headerName: "Наименование",
      cellRendererParams: {
        suppressCount: true,
      },
      valueFormatter: (params: ValueFormatterParams) => params.value,
    };
  }, []);

  const getDataPath = useCallback((data: any) => data.path, []);

  const onGridReady = useCallback(() => {
    gridRef.current?.api?.sizeColumnsToFit({
      defaultMaxWidth: 150,
    });
  }, []);

  const onCellValueChanged = useCallback(
    (event: any) => {
      if (event.column.colId === "autocorrection_total") {
        const index = event.data.id;
        const value = +event.newValue;
        const originalValue = summaryTable.rows[index]?.autocorrection_total;

        setAutocorrectionValues((prev) => {
          const newValues = [...prev];
          newValues[index] = value;
          return newValues;
        });

        setChangedRows((prev) => {
          const newChangedRows = new Map(prev);
          if (value !== originalValue) {
            newChangedRows.set(index, value);
          } else {
            newChangedRows.delete(index);
          }
          return newChangedRows;
        });
      }
    },
    [summaryTable.rows]
  );

  const reportTable = (
    <div style={{ width: "100%", height: "calc(100dvh - 108px)" }}>
      <AgGridReact
        ref={gridRef}
        rowData={reportTableRows}
        columnDefs={reportTableColumns}
        defaultColDef={defaultColDef}
        autoSizeStrategy={autoSizeStrategy}
        autoGroupColumnDef={autoGroupColumnDef}
        treeData={true}
        getDataPath={getDataPath}
        onGridReady={onGridReady}
        onCellValueChanged={onCellValueChanged}
        getRowStyle={getRowStyle}
      />
    </div>
  );

  const tableNode = summaryTable.rows.length ? reportTable : <>Таблица пуста</>;

  const hasAutocorrectionColumn =
    summaryTable.rows.length > 0 && summaryTable.rows[0].autocorrection_total !== undefined;

  const additionalButtons =
    isFiltersValid && hasAutocorrectionColumn ? (
      <div className="d-flex flex-wrap gap-2 w-full md-w-fit">
        <button
          className="btn btn-primary grow gap-2"
          onClick={onSaveAutocorrection}
          disabled={isFormLoading || !isFiltersValid || !isAutocorrectionChanged}
        >
          Сохранить автокорректировку
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      </div>
    ) : undefined;

  return (
    <>
      <PageLayout
        children={tableNode}
        header={"Сводная таблица"}
        search={false}
        filters={true}
        sorters={false}
        buttonText={"Скачать в Excel"}
        buttonAction={onExcelDownload}
        additionalButton={additionalButtons}
      />
    </>
  );
}
